import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ListRefund } from '../AdminClasses';
import { Button, Icon, Loader, Message, Modal, Table, Toolbar, useFrontier } from 'react-frontier2';
import { fromUnix } from '@arema/components/Util';
import { Money } from '@arema/components';
import { SetLoading } from '@arema/components/Classes';
import { useUser } from '../AdminHooks';
import UserAccess from '../UserAccess';
import API from '../API';

interface CompleteRefundModalProps{
	refunds: {
		refund_id: number,
		order_id: number,
		direct_refund?: boolean,
		date_created: number,
		total: number,
	}[],
	onClose?: ()=>void,
	onCompleted: (refunds: number[])=>void,
	open?: boolean,
	stp?: boolean,
	completed?: boolean,
}

var CompleteRefundModal = (props: CompleteRefundModalProps)=>{
	var { t } = useTranslation();
	var { hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [selectedRefunds, setSelectedRefunds] = useState<number[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [stp, setStp] = useState<boolean>(false);
	var [loadingStp, setLoadingStp] = useState<boolean>(false);

	useEffect(()=>{
		setSelectedRefunds([]);
		setStp(false);
		setLoadingStp(false);
	}, [props.open]);

	var showSTP = (refunds: number[])=>{
		return (setLoading: SetLoading)=>{
			if(!hasAccess(UserAccess.CONTABILIDAD.DOWNLOAD_STP_FILE)){
				if(props.onClose) props.onClose();
				return Toast.error(t('common.no_access_action'));
			}
			setModalPrompts(null);
			setSelectedRefunds(refunds);
			setStp(true);
			setLoadingStp(true);
			API.downloadRefundZip(refunds).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
			}).catch(err=>{
				setModalPrompts([t('common.unexpected_code', { code: 'LCL-COMPLRFMDL-2' })]);
			}).finally(()=>{
				setLoadingStp(false);
			});
		}
	}

	var sendComplete = (refunds: number[], force: boolean = false)=>{
		if(!props.onCompleted) return;
		return (setLoading: SetLoading)=>{
			if(!hasAccess(UserAccess.ORDERS.REFUND_COMPLETE)){
				if(props.onClose) props.onClose();
				return Toast.error(t('common.no_access_action'));
			}
			setModalPrompts(null);
			if(props.stp && !force){
				return showSTP(refunds)(setLoading);
			}

			setLoading(true);
			API.completeRefunds(refunds).then(res=>{
				if(res.error) return Toast.error(res.message);
				Toast.success(t('refund_list.refunds_completed'));
				props.onCompleted(res.data.completed);
				if(props.onClose) props.onClose();
			}).catch(err=>{
				Toast.error(t('common.unexpected_code', { code: 'LCL-COMPLRFMDL-1' }));
			}).finally(()=>{
				setLoading(false);
			})
		}
	}

	var sendSelection = (setLoading: SetLoading)=>{
		var refn = props.refunds.filter(a=>selectedRefunds.indexOf(a.refund_id)!=-1);
		if(props.stp){
			refn = refn.filter(a=>!a.direct_refund);
		}
		if(refn.length==0) return;
		return sendComplete(refn.map(a=>a.refund_id))(setLoading);
	}

	return <>
		<Modal size='mini' open={stp}>
			<Modal.Header>Descarga STP</Modal.Header>
			{stp && <Modal.Content>
				<div style={{ textAlign: 'center', fontSize: 16 }}>{t('modals.stp.downloaded')}</div>
				<Message type='warning' centered style={{ marginTop: 15 }}>
					<div className="header">{t('modals.stp.confirm_title')}</div>
					<Trans i18nKey={'modals.stp.instructions'} />
					<br />
					{loadingStp ? (
						<Loader inline style={{ marginTop: 15 }} />
					) : !modalPrompts || modalPrompts.length<=0 ? (
						<Button color='black' onClick={sendComplete(selectedRefunds, true)} disabled={modalPrompts && modalPrompts.length>0} text={t('modals.stp.confirm_title')} style={{ marginTop: 10 }} />
					) : (
						<Button basic text={t('modals.stp.retry_download')} style={{ marginTop: 15 }} color='black' onClick={showSTP(selectedRefunds)} />	
					)}
				</Message>
				<Message type='error' list={modalPrompts} />
				{(!modalPrompts || modalPrompts.length<=0) && (
					<Button basic text={t('modals.stp.retry_download')} disabled={loadingStp} size='tiny' style={{ display: 'block', width: 150, margin: 'auto', marginTop: 15 }} onClick={showSTP(selectedRefunds)} />
				)}
			</Modal.Content>}
		</Modal>
		<Modal size='small' open={props.open && !!props.refunds} onClose={props.onClose}>
			<Modal.Header>Seleccionar reembolsos</Modal.Header>
			<Toolbar>
				<Toolbar.Item text={t('common.select_all')} iconName={'check-circle'} onClick={()=>{
					setSelectedRefunds(props.refunds.map(a=>a.refund_id));
				}} />
				<Toolbar.Item text={t('common.deselect_all')} iconName={'trash'} onClick={()=>{
					setSelectedRefunds([]);
				}} />
			</Toolbar>
			{props.open && <Modal.Content>
				<Table fitted selectable style={{ marginTop: -20 }}>
					<Table.Head 
						data={[[
							<div style={{ textAlign: 'center' }}>
								<i className="checkmark icon" style={{ marginRight: 0 }}></i>
							</div>,
							'ID', 'Folio', 'Tipo', 'Fecha', 'Cantidad'
						]]}
					/>
					<Table.Body>
						{props.refunds.filter(a=>props.stp ? !a.direct_refund : true).map(a=>{
							var selected = selectedRefunds.indexOf(a.refund_id) !== -1;
							return <Table.Row
								onClick={()=>{
									var sp = [...selectedRefunds];
									if(!selected){
										sp.push(a.refund_id);
									}else{
										sp = sp.filter(r=>r!==a.refund_id);
									}
									setSelectedRefunds(sp);
								}}
								centeredIndexes={[0]}
								collapsingIndexes={[0, 1, 2, 4, 5]}
								data={[
									<Icon name={selected ? 'check-circle' : 'circle'} solid={!!selected} fitted style={{ fontSize: 16 }} />,
									a.refund_id, 
									a.order_id, 
									t(`refund_list.${a.direct_refund ? 'direct_refund': 'bank_transfer'}`),
									fromUnix(a.date_created).toFormat('dd/MMM/yy'), 
									<Money value={a.total} />,
								]}
							/>
						})}
					</Table.Body>
				</Table>
				{!!props.stp && (
					<Message text='Se descargará el archivo STP con la información de trasnferencia de los reembolsos seleccionados' type='info' centered style={{ marginTop: 10 }} />
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={props.onClose} />
				<Button text={props.stp ? t('common.continue') : t('common.complete')} color='black' onClick={sendSelection} disabled={(!selectedRefunds || selectedRefunds.length == 0)} />
			</Modal.Actions>
		</Modal>
	</>
}

export default CompleteRefundModal;